import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  Alert,
  Modal,
  ScrollView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GestureDetector, type NativeGesture } from 'react-native-gesture-handler';
import EventCard from '../components/EventCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import TonyteBackground from '../components/TonyteBackground';
import { useFeed, useActivityInterested, removeActivityFromFeedCache, updateActivityInFeedCache } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import { useCityContext } from '../context/CityContext';
import { useDateContext } from '../context/DateContext';
import { normalizeCityKey } from '../utils/cities';
import { Colors, Typography, Spacing, Radius, Shadow } from '../theme';
import { FIRESTORE_COLLECTIONS } from '../firebase';
import type { FeedFocus, TonyteActivity, RootStackParamList } from '../types';
import { deleteEventFromFirestore, ensureInterestedAndAddToCalendar, ensureInterestedAndJoinEventChat, getLumaRegistrationUrl, isEventChatExpiredError, registerOnLumaThen, shareActivityExternally } from '../utils/activityActions';
import EventShareSheet from '../components/EventShareSheet';
import EventCarousel, { CAROUSEL_CARD_WIDTH } from '../components/EventCarousel';
import TopPicksModule from '../components/TopPicksModule';
import PromotionsSection from '../components/PromotionsSection';
import ContinuedEventsCarousel from '../components/ContinuedEventsCarousel';
import FeaturedEventModal from '../components/FeaturedEventModal';
import { canManageEvent, type ManageEventUser } from '../utils/eventPermissions';
import { requestNotificationPermission } from '../utils/permissions';
import { showTonyteToast } from '../utils/tonyteToast';
import TonyteModal from '../components/TonyteModal';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen, type AnalyticsSource } from '../utils/eventAnalytics';
import {
  addDays,
  debugActivityDateDecision,
  findNextActivityDate,
  isActivityVisibleForSelectedDate,
  partitionContinuedActivities,
  parseActivityDate,
  shouldDebugFeedDates,
  sortActivitiesByStart,
  toLocalDateKey,
} from '../utils/activityDates';
import { type FeedChipId, FEED_CHIPS } from '../utils/eventTags';
import { useLocationCoords, useLocationPermission, ensureLocationPermission } from '../hooks/useLocation';
import type { LocationCoords } from '../services/locationEngine';
import { useI18n } from '../i18n';
import { isEventActivity } from '../utils/activityKind';
import { searchFeedEvents, searchTokens } from '../utils/feedSearch';
import FeedSearchBar from '../components/FeedSearchBar';
import { haversineKm } from '../utils/geo';
import { buildFeedRows, type FeedRow } from '../utils/feedRows';
import { useWeeklyFeatured } from '../hooks/useWeeklyFeatured';
import { useTopPicks } from '../hooks/useTopPicks';
import { useFeedPromotions } from '../hooks/usePromotions';
import type { PublishedPromotion } from '../utils/promotions';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';

interface Props {
  userId: string | null;
  scrollGesture?: NativeGesture;
  focusFeed?: FeedFocus;
}


function isAdminOrCoAdmin(user: ManageEventUser | null | undefined) {
  return user?.role === 'admin' || user?.role === 'co-admin';
}

function formatShortDate(date: Date, locale = 'en-US'): string {
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

function getDateExclusionReason(activity: TonyteActivity, selectedFeedDate: Date, now: Date): string {
  const parsed = parseActivityDate(activity);
  if (!parsed.startAt || !parsed.endAt) return 'excluded_unparseable_date';

  const selectedKey = toLocalDateKey(selectedFeedDate);
  const todayKey = toLocalDateKey(now);
  const overlapsSelectedDate =
    toLocalDateKey(parsed.startAt) <= selectedKey &&
    toLocalDateKey(parsed.endAt) >= selectedKey;

  if (!overlapsSelectedDate) return 'excluded_not_on_selected_date';
  if (selectedKey === todayKey && parsed.endAt.getTime() < now.getTime()) {
    return 'excluded_expired_today';
  }
  return 'excluded_by_date';
}

function buildActivityTokenText(activity: TonyteActivity): string {
  return [
    activity.title,
    activity.description,
    activity.category,
    activity.sourceType,
    ...(activity.tags || []),
    ...(activity.musicGenres || []),
    activity.venueName,
    activity.clubName,
  ].filter(Boolean).join(' ').toLowerCase();
}

function getActivityCoords(activity: TonyteActivity): { latitude: number; longitude: number } | null {
  const raw = activity as TonyteActivity & {
    latitude?: number; longitude?: number; lat?: number; lng?: number; location?: any;
  };
  if (typeof activity.coordinates?.lat === 'number' && typeof activity.coordinates?.lng === 'number' &&
      Number.isFinite(activity.coordinates.lat) && Number.isFinite(activity.coordinates.lng)) {
    return { latitude: activity.coordinates.lat, longitude: activity.coordinates.lng };
  }
  if (typeof raw.latitude === 'number' && typeof raw.longitude === 'number' &&
      Number.isFinite(raw.latitude) && Number.isFinite(raw.longitude)) {
    return { latitude: raw.latitude, longitude: raw.longitude };
  }
  if (typeof raw.lat === 'number' && typeof raw.lng === 'number' &&
      Number.isFinite(raw.lat) && Number.isFinite(raw.lng)) {
    return { latitude: raw.lat, longitude: raw.lng };
  }
  if (typeof raw.location?.latitude === 'number' && typeof raw.location?.longitude === 'number' &&
      Number.isFinite(raw.location.latitude) && Number.isFinite(raw.location.longitude)) {
    return { latitude: raw.location.latitude, longitude: raw.location.longitude };
  }
  return null;
}

// "Now" filter: event is currently active, with category-aware fallback duration.
function isNowActive(activity: TonyteActivity): boolean {
  const now = new Date();
  const parsed = parseActivityDate(activity);
  if (!parsed.startAt) return false;
  const startAt = parsed.startAt;

  // Use explicit end time if available.
  const fields = activity as any;
  const hasExplicitEnd = Boolean(
    activity.endTime ||
    (activity.endDate && activity.endDate !== activity.startDate) ||
    fields.endAt || fields.endsAt || fields.endDateTime,
  );
  if (hasExplicitEnd && parsed.endAt) {
    return startAt.getTime() <= now.getTime() && parsed.endAt.getTime() >= now.getTime();
  }

  // No explicit end — apply category-aware fallback duration.
  const cat = activity.category?.toLowerCase();
  const tokenText = buildActivityTokenText(activity);
  let endAt: Date;
  if (
    cat === 'nightlife' || activity.sourceType === 'club' ||
    /\b(concert|performance|show|live music|gig)\b/.test(tokenText)
  ) {
    endAt = new Date(startAt.getTime() + 6 * 60 * 60 * 1000);
  } else if (
    cat === 'culture' ||
    /\b(exhibition|museum|gallery|festival|tour)\b/.test(tokenText)
  ) {
    endAt = new Date(startAt);
    endAt.setHours(23, 59, 59, 999);
  } else {
    endAt = new Date(startAt.getTime() + 3 * 60 * 60 * 1000);
  }
  return startAt.getTime() <= now.getTime() && endAt.getTime() >= now.getTime();
}

function getEventStartHour(activity: TonyteActivity): number | null {
  const match = activity.startTime?.trim().match(/^(\d{1,2})(?::(\d{2}))?/);
  if (match) {
    const hour = Number(match[1]);
    return Number.isFinite(hour) && hour >= 0 && hour <= 23 ? hour : null;
  }

  const parsed = parseActivityDate(activity);
  return parsed.startAt ? parsed.startAt.getHours() : null;
}

const NEARBY_RADIUS_KM = 4;

function matchesFeedChip(
  activity: TonyteActivity,
  chipId: FeedChipId,
  userCoords: LocationCoords | null,
): boolean {
  // Direct tag match for non-smart filters (smart filters are always computed dynamically).
  if (chipId !== 'now' && chipId !== 'nearby' && chipId !== 'tonight') {
    const activityTags: string[] = activity.tags || [];
    if (activityTags.includes(chipId)) return true;
  }
  const tokenText = buildActivityTokenText(activity);
  switch (chipId) {
    case 'now':
      return isNowActive(activity);
    case 'tonight': {
      const startHour = getEventStartHour(activity);
      return startHour !== null && startHour >= 18;
    }
    case 'parties':
      return activity.category === 'nightlife'
        || activity.sourceType === 'club'
        || /\b(party|parties|nightclub|rave)\b/.test(tokenText);
    case 'social':
      return activity.category === 'social';
    case 'nearby': {
      if (!userCoords) return false;
      const actCoords = getActivityCoords(activity);
      if (!actCoords) return false;
      return haversineKm(userCoords.latitude, userCoords.longitude, actCoords.latitude, actCoords.longitude) <= NEARBY_RADIUS_KM;
    }
    case 'high-energy':
      return activity.category === 'nightlife'
        || activity.category === 'adventure'
        || /\b(dance|techno|rave|festival|extreme|workout|high energy)\b/.test(tokenText);
    case 'food':
      return activity.category === 'food'
        || /\b(food|dinner|drinks|bar|cocktail|restaurant|brunch|coffee|cafe)\b/.test(tokenText);
    case 'activities':
      return activity.category === 'adventure'
        || /\b(sport|hike|walk|outdoor|yoga|gym|run|activity)\b/.test(tokenText);
    case 'chill':
      return /\b(chill|relax|coffee|cafe|gallery|museum|culture|acoustic|quiet)\b/.test(tokenText);
    case 'date-idea':
      return /\b(date|romantic|dinner|wine|cocktail|jazz|intimate|couple)\b/.test(tokenText);
    case 'solo-friendly':
      return /\b(solo|newcomer|welcome|language|student|erasmus|meet)\b/.test(tokenText);
    case 'student':
      return /\b(student|students|campus|university|uni|erasmus|exchange)\b/.test(tokenText);
    case 'free':
      return activity.isFree === true;
  }
}


const FeedScreen: React.FC<Props> = ({ userId, scrollGesture, focusFeed }) => {
  if (__DEV__) console.count('[Perf][Render] FeedScreen');
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { width: viewportWidth } = useWindowDimensions();
  const desktopColumns = Platform.OS === 'web' && viewportWidth >= 1480 ? 3 : Platform.OS === 'web' && viewportWidth >= 1024 ? 2 : 1;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { profile, loading: profileLoading } = useProfile(userId);
  const feedOptions = useMemo(
    () => ({ includeAdminOnlyCities: profile?.role === 'admin' }),
    [profile?.role],
  );
  const { activities, loading, error, refresh } = useFeed(feedOptions);
  const { selectedCityKey, setSelectedCityKey, visibleCities } = useCityContext();
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [activeChips, setActiveChips] = useState<Set<FeedChipId>>(new Set());
  // Feed search (FeedSearchBar debounces it). While a query is active the list
  // shows matches from every upcoming day in the city instead of the selected
  // day/chips, and the Top Picks slot steps aside.
  const [searchQuery, setSearchQuery] = useState('');
  // Backed by the app-wide Location Engine (src/services/locationEngine.ts)
  // — a single always-on watcher started once at launch. This screen only
  // reads the live snapshot; it never starts/stops GPS itself.
  const userCoords = useLocationCoords();
  const locationStatus = useLocationPermission();
  const { selectedDate, setSelectedDate, today, tomorrow, dateMode } = useDateContext();
  // One-shot: an event push notification (see notificationNav.ts) opens the
  // Feed on its day with no chip filters hiding what it announced. Keyed by
  // `nonce` so a second tap while already here still re-applies it.
  const lastFocusNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!focusFeed || focusFeed.nonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusFeed.nonce;
    const [year, month, day] = focusFeed.date.split('-').map(Number);
    // DateContext clamps past/invalid dates to today.
    setSelectedDate(year && month && day ? new Date(year, month - 1, day) : new Date());
    setActiveChips(new Set((focusFeed.chips || []) as FeedChipId[]));
  }, [focusFeed, setSelectedDate]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [shareSheetActivity, setShareSheetActivity] = useState<TonyteActivity | null>(null);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  }, [refresh]);

  const selectedFeedDate = selectedDate;
  const dateOptions = useMemo(
    () => Array.from({ length: 30 }, (_, index) => addDays(today, index)),
    [today],
  );

  const cityLabel = visibleCities.find(c => c.value === selectedCityKey)?.label || visibleCities[0]?.label || 'Dresden';
  const dateVisibleActivities = useMemo(() => {
    const now = new Date();
    const debugFeedDates = shouldDebugFeedDates();
    if (debugFeedDates) {
      console.log('[Tonyte][RN][FeedDate] feed selection', {
        currentDeviceDate: now.toISOString(),
        currentLocalDate: toLocalDateKey(now),
        currentLocalTime: now.toLocaleTimeString(),
        selectedFeedDate: toLocalDateKey(selectedFeedDate),
        dateMode,
        selectedCityKey,
      });
    }

    return activities.filter(a => {
      if (!isEventActivity(a)) return false;
      const activityCityKey = normalizeCityKey(a.cityKey || a.city);
      const matchesCity = !selectedCityKey || activityCityKey === selectedCityKey;
      const matchesDate = isActivityVisibleForSelectedDate(a, selectedFeedDate, now);
      if (debugFeedDates) {
        debugActivityDateDecision(
          a,
          selectedFeedDate,
          matchesCity && matchesDate,
          now,
          !matchesCity
            ? 'excluded_by_city'
            : matchesDate
              ? 'included_on_selected_date'
              : getDateExclusionReason(a, selectedFeedDate, now),
        );
      }
      return matchesCity && matchesDate;
    }).sort(sortActivitiesByStart);
  }, [activities, dateMode, selectedCityKey, selectedFeedDate]);

  // If the last event today has ended, advance to the nearest future day
  // with events in the selected city. The shared DateContext keeps Feed and
  // Map on the same truthful date instead of rendering tomorrow under Today.
  useEffect(() => {
    if (loading || dateMode !== 'today' || dateVisibleActivities.length > 0) return;
    const cityEvents = activities.filter(activity => (
      isEventActivity(activity) &&
      (!selectedCityKey || normalizeCityKey(activity.cityKey || activity.city) === selectedCityKey)
    ));
    const nextDate = findNextActivityDate(cityEvents, today, new Date(), 30);
    if (nextDate) setSelectedDate(nextDate);
  }, [activities, dateMode, dateVisibleActivities.length, loading, selectedCityKey, setSelectedDate, today]);

  const nearbyActive = activeChips.has('nearby');

  const filtered = useMemo(() => {
    const now = new Date();
    const debugFeedDates = shouldDebugFeedDates();
    const result = dateVisibleActivities.filter(a => {
      const selectedFeedTags = [...activeChips];
      const matchesChips = selectedFeedTags.length === 0
        ? true
        : selectedFeedTags.every(chipId => matchesFeedChip(a, chipId, userCoords));
      if (debugFeedDates) {
        debugActivityDateDecision(
          a,
          selectedFeedDate,
          matchesChips,
          now,
          matchesChips ? 'included_in_feed' : 'excluded_by_chip_filter',
        );
      }
      return matchesChips;
    });

    // Sort by distance ascending when Nearby is the only or primary active chip.
    if (nearbyActive && userCoords) {
      result.sort((a, b) => {
        const ca = getActivityCoords(a);
        const cb = getActivityCoords(b);
        if (!ca && !cb) return 0;
        if (!ca) return 1;
        if (!cb) return -1;
        const da = haversineKm(userCoords.latitude, userCoords.longitude, ca.latitude, ca.longitude);
        const db = haversineKm(userCoords.latitude, userCoords.longitude, cb.latitude, cb.longitude);
        return da - db;
      });
    } else {
      result.sort(sortActivitiesByStart);
    }
    return result;
  }, [activeChips, dateVisibleActivities, nearbyActive, selectedFeedDate, userCoords]);

  const searchActive = searchTokens(searchQuery).length > 0;
  const searchResults = useMemo(
    () => (searchActive ? searchFeedEvents(activities, searchQuery, { cityKey: selectedCityKey }) : []),
    [activities, searchActive, searchQuery, selectedCityKey],
  );
  const { continued: continuedEvents, startingOnSelectedDate } = useMemo(
    () => partitionContinuedActivities(filtered, selectedFeedDate),
    [filtered, selectedFeedDate],
  );
  const listEvents = searchActive ? searchResults : startingOnSelectedDate;

  const currentUser = useMemo<ManageEventUser | null>(
    () => (profile ? { ...profile, uid: userId } : userId ? { uid: userId } : null),
    [profile, userId],
  );
  const canManageFeatured = isAdminOrCoAdmin(currentUser);
  const [featuredEditorActivity, setFeaturedEditorActivity] = useState<TonyteActivity | null>(null);

  // --- Discovery carousels -------------------------------------------------
  // Both are derived from the same already-fetched pool as the regular feed
  // (`activities`), so neither adds Firestore reads of its own for events, and
  // neither removes anything from the regular list below.
  const weeklyFeatured = useWeeklyFeatured(activities, selectedCityKey);
  // Seasonal/editorial collections: remotely published, resolved against the
  // same pool (no per-event reads) and independent of the feed's day/chips.
  const feedPromotions = useFeedPromotions(activities, selectedCityKey);
  const topPicksCandidates = useMemo(() => activities.filter(isEventActivity), [activities]);
  // An event is never in both carousels: whatever is featured is excluded from
  // the personalised picks (by both document id and resolved event id).
  const featuredExcludeIds = useMemo(
    () => weeklyFeatured.flatMap(activity => [activity.id, getResolvedEventId(activity)]),
    [weeklyFeatured],
  );
  const topPicks = useTopPicks({
    userId,
    profile,
    profileReady: !profileLoading,
    candidates: topPicksCandidates,
    excludeEventIds: featuredExcludeIds,
    cityKey: selectedCityKey,
    userCoords,
  });
  const carouselCardWidth = desktopColumns > 1 ? 340 : CAROUSEL_CARD_WIDTH;
  const openTopPicksSeeAll = useCallback(() => {
    navigation.navigate('AllTopPicks');
  }, [navigation]);

  const openEvent = useCallback((activity: TonyteActivity, source: AnalyticsSource) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);
  const handleFeaturedCardPress = useCallback(
    (activity: TonyteActivity) => openEvent(activity, ANALYTICS_SOURCES.WEEKLY_FEATURED),
    [openEvent],
  );
  const handleOpenChat = useCallback((activity: TonyteActivity) => {
    navigation.navigate('ChatDetail', {
      chatId: getResolvedEventId(activity),
      name: activity.title,
      kind: 'event',
      photo: activity.coverPhoto || activity.coverImageUrl || activity.imageUrl,
    });
  }, [navigation]);
  const handleContinuedCardPress = useCallback(
    (activity: TonyteActivity) => openEvent(activity, ANALYTICS_SOURCES.REGULAR_FEED),
    [openEvent],
  );
  const openPromotion = useCallback((promotion: PublishedPromotion) => {
    navigation.navigate('Promotion', { promotionId: promotion.id });
  }, [navigation]);
  const openPromotionsAdmin = useCallback(() => navigation.navigate('AdminPromotions'), [navigation]);
  const handleTopPickCardPress = useCallback(
    (activity: TonyteActivity) => openEvent(activity, ANALYTICS_SOURCES.TOP_PICKS_FOR_YOU),
    [openEvent],
  );

  // Only events stored in `activities` carry the admin-writable featured fields.
  const openFeaturedEditor = useCallback((activity: TonyteActivity) => {
    if (!canManageFeatured || activity.sourceCollection !== FIRESTORE_COLLECTIONS.activities) return;
    setFeaturedEditorActivity(activity);
  }, [canManageFeatured]);
  const handleFeaturedSaved = useCallback((activity: TonyteActivity, patch: Partial<TonyteActivity>) => {
    updateActivityInFeedCache({ ...activity, ...patch });
  }, []);

  const ListHeader = useMemo(
    () => (
      <View style={styles.feedHeader}>
        {desktopColumns === 1 ? <Text style={[styles.eyebrow, themedStyles.textMuted]}>{t('feed.eyebrow')}</Text> : null}
        <View style={styles.titleRow}>
          <Text style={[styles.headerTitle, desktopColumns > 1 && styles.desktopHeaderTitle, themedStyles.textPrimary]}>{t('feed.whatsOnIn')}{desktopColumns > 1 ? ` ${cityLabel}` : ''}</Text>
          {desktopColumns === 1 ? <TouchableOpacity
            activeOpacity={0.82}
            style={[styles.cityPill, themedStyles.cityPill]}
            onPress={() => setCityPickerOpen(true)}
          >
            <Icon name="map-pin" size={13} color={theme.colors.accentSecondary} style={{ marginRight: 5 }} />
            <Text style={[styles.cityPillText, themedStyles.accentSecondary]}>{cityLabel}</Text>
            <Icon name="chevron-down" size={13} color={theme.colors.accentSecondary} style={{ marginLeft: 5 }} />
          </TouchableOpacity> : null}
        </View>

        <PromotionsSection
          items={feedPromotions}
          desktop={desktopColumns > 1}
          onPress={openPromotion}
          onManage={canManageFeatured ? openPromotionsAdmin : undefined}
          testID="feed-promotions"
        />

        <EventCarousel
          title={t('feed.featuredThisWeek')}
          events={weeklyFeatured}
          userId={userId}
          cardWidth={carouselCardWidth}
          showPartner
          onCardPress={handleFeaturedCardPress}
          onOpenChat={handleOpenChat}
          onAdminPress={canManageFeatured ? openFeaturedEditor : undefined}
          testID="featured-this-week"
        />

        {/* Day + tag filters only shape the list below (Collections and
            Featured ignore them), so they sit right above it, with search. */}
        <View style={[styles.segmented, desktopColumns > 1 && styles.desktopSegmented, themedStyles.segmented]}>
          {[
            { label: t('feed.today'), mode: 'today' as const },
            { label: t('feed.tomorrow'), mode: 'tomorrow' as const },
            { label: dateMode === 'custom' ? formatShortDate(selectedDate, ({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US') : t('feed.date'), mode: 'custom' as const },
          ].map(item => {
            const active = dateMode === item.mode;
            return (
            <TouchableOpacity
              key={item.mode}
              onPress={() => {
                if (item.mode === 'today') {
                  setSelectedDate(today);
                  return;
                }
                if (item.mode === 'tomorrow') {
                  setSelectedDate(tomorrow);
                  return;
                }

                setDatePickerOpen(true);
              }}
              activeOpacity={0.86}
              style={[
                styles.segment,
                active ? styles.segmentActive : [styles.segmentInactive, themedStyles.segmentInactive],
              ]}
            >
              {item.mode === 'custom' ? (
                <Icon name="calendar" size={11} color={active ? '#fff' : theme.colors.icon} />
              ) : null}
              <Text
                style={[
                  styles.segmentText,
                  themedStyles.textSecondary,
                  active && styles.segmentTextActive,
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          );})}
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.quickRow}
          contentContainerStyle={styles.quickRowContent}
        >
          {activeChips.size > 0 ? (
            <TouchableOpacity
              onPress={() => setActiveChips(new Set())}
              activeOpacity={0.84}
              style={[styles.quickChip, themedStyles.quickChip, styles.quickChipClear]}
            >
              <Icon name="x" size={13} color={theme.colors.iconSubtle} />
              <Text style={[styles.quickText, themedStyles.textSecondary]}>{t('common.clear')}</Text>
            </TouchableOpacity>
          ) : null}
          {FEED_CHIPS.map(item => {
            const selected = activeChips.has(item.id);
            return (
              <TouchableOpacity
                key={item.id}
                onPress={() => {
                  setActiveChips(current => {
                    const next = new Set(current);
                    if (next.has(item.id)) { next.delete(item.id); } else { next.add(item.id); }
                    return next;
                  });
                  if (item.id === 'nearby' && locationStatus === 'idle') {
                    void ensureLocationPermission();
                  }
                }}
                activeOpacity={0.84}
                style={[styles.quickChip, themedStyles.quickChip, selected && styles.quickChipActive, selected && themedStyles.quickChipActive]}
              >
                <Icon
                  name={item.icon}
                  size={13}
                  color={selected ? theme.colors.filterActiveIcon : theme.colors.iconSubtle}
                />
                <Text style={[styles.quickText, themedStyles.textSecondary, selected && styles.quickTextActive, selected && themedStyles.quickTextActive]}>
                  {t(`feedFilters.${item.id}`, item.label)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {nearbyActive && locationStatus !== 'granted' ? (
          <View style={[styles.locationBanner, themedStyles.locationBanner]}>
            <Icon name="map-pin" size={15} color={theme.colors.accentSecondary} />
            <Text style={[styles.locationBannerText, themedStyles.textSecondary]}>
              {locationStatus === 'requesting'
                ? t('feed.gettingLocation')
                : locationStatus === 'denied' || locationStatus === 'blocked'
                  ? t('feed.locationDenied')
                  : t('feed.enableLocation')}
            </Text>
            {locationStatus === 'idle' ? (
              <TouchableOpacity onPress={() => void ensureLocationPermission()} activeOpacity={0.8} style={styles.locationBannerBtn}>
                <Text style={[styles.locationBannerBtnText, themedStyles.accentSecondary]}>{t('common.enable')}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        <FeedSearchBar onSearch={setSearchQuery} testID="feed-search" />

        {!searchActive ? (
          <ContinuedEventsCarousel
            events={continuedEvents}
            selectedDate={selectedFeedDate}
            onPress={handleContinuedCardPress}
            testID="continued-events"
          />
        ) : null}

        <View style={styles.allEventsRow}>
          <Text style={[styles.allEventsText, themedStyles.textSecondary]}>{searchActive ? t('feed.searchResults') : t('feed.allEvents')}</Text>
          <Text style={[styles.allEventsCount, themedStyles.textMuted]}>{listEvents.length}</Text>
        </View>

        {error ? (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={refresh}>
              <Text style={styles.retryText}>{t('common.tapToRetry')}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    ),
    [activeChips, canManageFeatured, carouselCardWidth, cityLabel, continuedEvents, dateMode, desktopColumns, error, feedPromotions, handleContinuedCardPress, handleFeaturedCardPress, handleOpenChat, listEvents.length, language, locationStatus, nearbyActive, openFeaturedEditor, openPromotion, openPromotionsAdmin, refresh, searchActive, selectedDate, selectedFeedDate, setSelectedDate, t, theme.colors, themedStyles, today, tomorrow, userId, weeklyFeatured],
  );

  // Stable, item-taking handlers — NOT re-created per row. renderItem used to
  // build a fresh closure per item on every call, which defeated
  // FeedCardWrapper's/EventCard's React.memo (new function prop => memo
  // bails out => the whole card, including its translation/image
  // effects, re-renders) any time `filtered` got a new array reference for
  // any reason (e.g. a GPS tick), even when that item's own data hadn't
  // changed at all.
  const handleCardPress = useCallback(
    (activity: TonyteActivity) => openEvent(activity, ANALYTICS_SOURCES.REGULAR_FEED),
    [openEvent],
  );
  const handleEditCard = useCallback((activity: TonyteActivity) => {
    navigation.navigate('UploadEvent', { event: activity });
  }, [navigation]);
  const handleShareInApp = useCallback((activity: TonyteActivity) => {
    setShareSheetActivity(activity);
  }, []);

  // The list is rendered as rows (one card per row on mobile, `desktopColumns`
  // cards per row on wide web) so the full-width "Top Picks for You" carousel
  // can sit between two groups of cards in every layout — a FlatList with
  // numColumns > 1 can't hold a full-width item mid-grid. The regular feed
  // itself is untouched: every filtered event is still in here, in order.
  const showTopPicksSlot = topPicks.status !== 'empty' && !searchActive;
  const listRows = useMemo(
    () => buildFeedRows(listEvents, desktopColumns, showTopPicksSlot),
    [desktopColumns, listEvents, showTopPicksSlot],
  );

  const renderRow = useCallback(
    ({ item }: { item: FeedRow }) => {
      if (item.type === 'topPicks') {
        return (
          <TopPicksModule
            items={topPicks.items}
            status={topPicks.status === 'loading' ? 'loading' : topPicks.status === 'error' ? 'error' : 'ready'}
            mode={topPicks.mode}
            profile={profile}
            userId={userId}
            onRetry={topPicks.refresh}
            onCardPress={handleTopPickCardPress}
            onSeeAll={openTopPicksSeeAll}
            errorMessage={t('feed.topPicksError')}
            retryLabel={t('common.tapToRetry')}
            testID="top-picks-for-you"
          />
        );
      }
      const cards = item.items.map(activity => (
        <View key={activity.id} style={desktopColumns > 1 ? styles.desktopGridItem : undefined}>
          <FeedCardWrapper
            activity={activity}
            userId={userId}
            currentUser={currentUser}
            onPress={handleCardPress}
            onOpenChat={handleOpenChat}
            onEdit={handleEditCard}
            canManageFeatured={canManageFeatured && activity.sourceCollection === FIRESTORE_COLLECTIONS.activities}
            onFeaturedPress={openFeaturedEditor}
            onShareInApp={handleShareInApp}
          />
        </View>
      ));
      return desktopColumns > 1 ? <View style={styles.desktopGridRow}>{cards}</View> : <>{cards}</>;
    },
    [canManageFeatured, currentUser, desktopColumns, handleCardPress, handleOpenChat, handleEditCard, handleShareInApp, handleTopPickCardPress, openFeaturedEditor, openTopPicksSeeAll, profile, t, topPicks.items, topPicks.mode, topPicks.refresh, topPicks.status, userId],
  );

  if (loading && !refreshing) {
    return <LoadingSpinner appearanceAware label={t('feed.loadingEvents')} />;
  }

  const list = (
      <FlatList
        key={`feed-${desktopColumns}`}
        style={styles.scroller}
        data={listRows}
        scrollEnabled
        nestedScrollEnabled
        keyExtractor={item => item.key}
        renderItem={renderRow}
        // The promotions carousel (in this list's header) auto-scrolls, and on iOS each
        // of its scroll events let this list claim the touch — cancelling taps on Top
        // Picks and "See all" mid-press. Native scrolling is unaffected.
        disableScrollViewPanResponder
        ListHeaderComponent={ListHeader}
        contentContainerStyle={[
          styles.list,
          desktopColumns > 1 && styles.desktopList,
          { paddingBottom: desktopColumns > 1 ? 32 : Math.max(insets.bottom, 16) + 112 },
        ]}
        initialNumToRender={5}
        maxToRenderPerBatch={6}
        windowSize={7}
        removeClippedSubviews
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={Colors.sunset}
          />
        }
        ListEmptyComponent={
          !loading ? (
            searchActive ? (
              <EmptyState
                icon="search"
                title={t('feed.searchEmpty').replace('{query}', searchQuery.trim())}
                subtitle={t('feed.searchEmptySub')}
              />
            ) : (
              <EmptyState
                icon="calendar"
                title={t('feed.noEventsFound')}
                subtitle={t('feed.noEventsSub')}
              />
            )
          ) : null
        }
      />
  );

  return (
    <TonyteBackground appearanceAware style={[styles.container, themedStyles.container, { paddingTop: insets.top }]}>

      {scrollGesture ? (
        <GestureDetector gesture={scrollGesture}>{list}</GestureDetector>
      ) : list}

      <Modal
        visible={datePickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setDatePickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={[styles.dateModalBackdrop, themedStyles.modalBackdrop]}
          onPress={() => setDatePickerOpen(false)}
        >
          <TouchableOpacity
            activeOpacity={1}
            accessibilityViewIsModal
            style={[styles.dateModal, themedStyles.modal, { paddingBottom: Math.max(14, insets.bottom) }]}
          >
            <View style={[styles.dateModalHandle, themedStyles.modalHandle]} />
            <View style={styles.dateModalHeader}>
              <View style={styles.dateModalHeading}>
                <LinearGradient
                  colors={theme.isDark ? ['rgba(242,125,38,0.28)', 'rgba(38,208,206,0.14)'] : ['rgba(242,125,38,0.20)', 'rgba(21,155,154,0.10)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.dateModalIcon}
                >
                  <Icon name="calendar" size={20} color={Colors.sunset} />
                </LinearGradient>
                <View>
                  <Text style={[styles.dateModalEyebrow, themedStyles.accentSecondary]}>
                    {selectedDate.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { month: 'long', year: 'numeric' })}
                  </Text>
                  <Text style={[styles.dateModalTitle, themedStyles.textPrimary]}>{t('map.selectDate')}</Text>
                </View>
              </View>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('common.close')}
                style={[styles.dateModalClose, themedStyles.modalClose]}
                onPress={() => setDatePickerOpen(false)}
              >
                <Icon name="x" size={20} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.dateOptionsContent}>
              {dateOptions.map(option => {
                const key = toLocalDateKey(option);
                const selected = key === toLocalDateKey(selectedDate);
                const dayOffset = Math.round((option.getTime() - today.getTime()) / 86_400_000);
                const relativeLabel = dayOffset === 0 ? t('feed.today') : dayOffset === 1 ? t('feed.tomorrow') : null;
                return (
                  <TouchableOpacity
                    key={key}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    style={[styles.dateOption, themedStyles.dateOption, selected && styles.dateOptionActive, selected && themedStyles.dateOptionActive]}
                    onPress={() => {
                      setSelectedDate(option);
                      setDatePickerOpen(false);
                    }}
                  >
                    {selected ? <View style={styles.dateOptionAccent} /> : null}
                    <View style={[styles.dateNumberTile, themedStyles.dateNumberTile, selected && styles.dateNumberTileActive]}>
                      <Text style={[styles.dateNumber, themedStyles.dateNumber, selected && styles.dateNumberActive]}>{option.getDate()}</Text>
                    </View>
                    <View style={styles.dateOptionCopy}>
                      <View style={styles.dateOptionTitleRow}>
                        <Text style={[styles.dateOptionDay, themedStyles.textPrimary, selected && styles.dateOptionTextActive, selected && themedStyles.textPrimary]}>
                          {option.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { weekday: 'long' })}
                        </Text>
                        {relativeLabel ? (
                          <View style={[styles.relativeDateBadge, themedStyles.relativeDateBadge, selected && styles.relativeDateBadgeActive]}>
                            <Text style={[styles.relativeDateText, themedStyles.relativeDateText, selected && styles.relativeDateTextActive]}>{relativeLabel}</Text>
                          </View>
                        ) : null}
                      </View>
                      <Text style={[styles.dateOptionText, themedStyles.textSecondary, selected && styles.dateOptionSubtextActive, selected && themedStyles.dateOptionSubtextActive]}>
                        {option.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { month: 'long', year: 'numeric' })}
                      </Text>
                    </View>
                    <View style={[styles.dateOptionIndicator, themedStyles.dateOptionIndicator, selected && styles.dateOptionIndicatorActive]}>
                      {selected ? <Icon name="check" size={14} color="#fff" /> : null}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={cityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCityPickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={styles.dateModalBackdrop}
          onPress={() => setCityPickerOpen(false)}
        >
          <TouchableOpacity activeOpacity={1} style={[styles.dateModal, styles.cityModal, themedStyles.modal]}>
            <View style={styles.dateModalHeader}>
              <Text style={[styles.dateModalTitle, themedStyles.textPrimary]}>{t('map.selectCity')}</Text>
              <TouchableOpacity onPress={() => setCityPickerOpen(false)}>
                <Icon name="x" size={18} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>
            {visibleCities.map(city => {
              const selected = city.value === selectedCityKey;
              return (
                <TouchableOpacity
                  key={city.value}
                  style={[styles.dateOption, styles.cityOption, themedStyles.dateOption, selected && styles.dateOptionActive]}
                  onPress={() => {
                    setSelectedCityKey(city.value);
                    setCityPickerOpen(false);
                  }}
                >
                  <Text style={[styles.dateOptionText, themedStyles.textSecondary, selected && { color: theme.colors.accent, fontWeight: Typography.extrabold }]}>
                    {city.label}
                  </Text>
                  {selected ? <Icon name="check" size={16} color={Colors.sunset} /> : null}
                </TouchableOpacity>
              );
            })}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
      {canManageFeatured && userId ? (
        <FeaturedEventModal
          activity={featuredEditorActivity}
          adminUid={userId}
          onClose={() => setFeaturedEditorActivity(null)}
          onSaved={handleFeaturedSaved}
        />
      ) : null}
      {shareSheetActivity && userId && (
        <EventShareSheet
          activity={shareSheetActivity}
          userId={userId}
          userName={profile?.name ?? 'Traveler'}
          userPhotoURL={profile?.photo ?? undefined}
          visible={Boolean(shareSheetActivity)}
          onClose={() => setShareSheetActivity(null)}
        />
      )}
    </TonyteBackground>
  );
};

// Wrapper to get interest state per card. Exported so other event lists
// (e.g. a promotion's collection screen) get the exact same card actions.
export const FeedCardWrapper: React.FC<{
  activity: TonyteActivity;
  userId: string | null;
  currentUser: ManageEventUser | null;
  onPress: (activity: TonyteActivity) => void;
  onOpenChat: (activity: TonyteActivity) => void;
  onEdit: (activity: TonyteActivity) => void;
  canManageFeatured: boolean;
  onFeaturedPress: (activity: TonyteActivity) => void;
  onShareInApp: (activity: TonyteActivity) => void;
}> = memo(({ activity, userId, currentUser, onPress, onOpenChat, onEdit, canManageFeatured, onFeaturedPress, onShareInApp }) => {
  const { t } = useI18n();
  const { interested, count, toggle, syncInterested } = useActivityInterested(
    getResolvedEventId(activity),
    userId,
    activity,
  );
  const canManage = canManageEvent(currentUser, activity);
  const supportsEventChat = activity.entryType === 'official_event' && activity.sourceType !== 'club';
  const [showInterestPrompt, setShowInterestPrompt] = useState(false);

  const handleChat = useCallback(async () => {
    if (!userId) return;
    try {
      await ensureInterestedAndJoinEventChat(activity, userId, false, t);
      syncInterested(true);
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      onOpenChat(activity);
    } catch (error) {
      if (isEventChatExpiredError(error)) {
        showTonyteToast(t('event.chatNoLongerAvailable'), 'info', t('event.chatNoLongerAvailableBody'));
      } else {
        showTonyteToast(t('event.couldNotJoinChat'), 'error', t('common.tryAgain'));
      }
    }
  }, [activity, onOpenChat, syncInterested, t, userId]);

  const handleAddToCalendar = useCallback(async () => {
    if (!userId) return;
    if (await ensureInterestedAndAddToCalendar(activity, userId, t)) syncInterested(true);
  }, [activity, syncInterested, t, userId]);

  const handleInterestPress = () => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }
    if (interested || !supportsEventChat) {
      void toggle();
      return;
    }
    setShowInterestPrompt(true);
  };
  const confirmDelete = () => {
    Alert.alert(t('event.deleteEventQuestion'), t('event.deleteEventConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: () => {
          removeActivityFromFeedCache(activity.id);
          void deleteEventFromFirestore(activity, userId || '').catch(() => {
            Alert.alert(t('event.couldNotDeleteEvent'), t('common.tryAgain'));
          });
        },
      },
    ]);
  };
  return (
    <>
      <EventCard
        activity={activity}
        onPress={() => onPress(activity)}
        onInterestPress={handleInterestPress}
        onSharePress={() => Alert.alert(t('event.shareEvent'), activity.title, [
          { text: t('event.shareOutsideTonyte'), onPress: () => void shareActivityExternally(activity, t) },
          { text: t('event.sendInTonyteChat'), onPress: () => onShareInApp(activity) },
          { text: t('common.cancel'), style: 'cancel' },
        ])}
        canManage={canManage}
        onEditPress={() => onEdit(activity)}
        onDeletePress={confirmDelete}
        canManageFeatured={canManageFeatured}
        onFeaturedPress={() => onFeaturedPress(activity)}
        interested={interested}
        interestedCount={count}
      />

      <TonyteModal
        visible={showInterestPrompt}
        title={t('event.joinEventChatQuestion')}
        message={getLumaRegistrationUrl(activity) ? `${t('event.joinEventChatPrompt')}\n\n${t('event.registerOnLumaFirst')}` : t('event.joinEventChatPrompt')}
        icon="message-circle"
        iconColor={Colors.turquoise}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: () => setShowInterestPrompt(false) },
          {
            label: t('event.interestedOnly'),
            variant: 'secondary',
            onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, toggle, t); },
          },
          {
            label: t('event.addToCalendar'),
            variant: 'teal',
            onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleAddToCalendar, t); },
          },
          {
            label: t('event.joinChat'),
            variant: 'primary',
            onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleChat, t); },
          },
        ]}
        onDismiss={() => setShowInterestPrompt(false)}
      />
    </>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  feedHeader: {
    paddingTop: 15,
    paddingBottom: 8,
  },
  eyebrow: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 9,
    lineHeight: 12,
    fontWeight: Typography.extrabold,
    letterSpacing: 3,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: 22,
    lineHeight: 27,
    fontWeight: Typography.extrabold,
  },
  desktopHeaderTitle: { fontSize: 21, lineHeight: 25 },
  desktopSegmented: { width: 330 },
  cityPill: {
    minWidth: 118,
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cityPillText: {
    color: Colors.turquoise,
    fontSize: 17,
    lineHeight: 21,
    fontWeight: Typography.extrabold,
  },
  segmented: {
    height: 35,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: 5,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    marginBottom: 12,
  },
  segment: {
    flex: 1,
    height: 25,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  segmentActive: {
    backgroundColor: '#ff8124',
  },
  segmentInactive: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  segmentText: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  segmentTextActive: {
    color: '#fff',
    fontWeight: Typography.extrabold,
  },
  quickRow: {
    marginBottom: 12,
  },
  quickRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingRight: 4,
  },
  quickChip: {
    height: 32,
    paddingHorizontal: 11,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  quickChipActive: {
    backgroundColor: 'rgba(242,125,38,0.20)',
    borderColor: 'rgba(242,125,38,0.62)',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.34,
    shadowRadius: 10,
    elevation: 5,
  },
  quickChipClear: {
    backgroundColor: 'rgba(255,255,255,0.035)',
    borderColor: 'rgba(255,255,255,0.10)',
  },
  quickText: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  quickTextActive: {
    color: '#fff',
  },
  locationBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.22)',
    backgroundColor: 'rgba(38,208,206,0.07)',
  },
  locationBannerText: {
    flex: 1,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 12,
    lineHeight: 17,
    fontWeight: Typography.medium,
  },
  locationBannerBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.45)',
    backgroundColor: 'rgba(38,208,206,0.15)',
  },
  locationBannerBtnText: {
    color: Colors.turquoise,
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  allEventsRow: {
    marginTop: 24,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  allEventsText: {
    color: 'rgba(255,255,255,0.54)',
    fontSize: 13,
    lineHeight: 16,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: Typography.extrabold,
  },
  allEventsCount: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  list: {
    paddingTop: 0,
    paddingHorizontal: Spacing.base,
  },
  desktopList: {
    width: '100%',
    maxWidth: 1440,
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  desktopGridRow: {
    gap: 16,
  },
  desktopGridItem: {
    flex: 1,
    minWidth: 0,
    marginBottom: 16,
  },
  scroller: {
    flex: 1,
  },
  errorBox: {
    margin: Spacing.base,
    padding: Spacing.base,
    backgroundColor: Colors.error + '18',
    borderRadius: Radius.md,
    borderWidth: 1,
    borderColor: Colors.error + '40',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  errorText: {
    color: Colors.error,
    fontSize: Typography.sm,
  },
  retryText: {
    color: Colors.sunset,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  dateModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(2,5,11,0.74)',
    justifyContent: 'flex-end',
  },
  dateModal: {
    maxHeight: '72%',
    marginHorizontal: 10,
    marginBottom: 10,
    borderRadius: 26,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    backgroundColor: '#0b111d',
    paddingHorizontal: 16,
    paddingTop: 9,
    ...Shadow.strong,
  },
  dateModalHandle: {
    width: 42,
    height: 4,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignSelf: 'center',
    marginBottom: 13,
  },
  dateModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  dateModalHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dateModalIcon: {
    width: 46,
    height: 46,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.28)',
  },
  dateModalEyebrow: {
    color: Colors.turquoise,
    fontSize: 10,
    lineHeight: 13,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    fontWeight: Typography.extrabold,
  },
  dateModalTitle: {
    color: Colors.textPrimary,
    fontSize: 22,
    lineHeight: 27,
    fontWeight: Typography.extrabold,
  },
  dateModalClose: {
    width: 40,
    height: 40,
    borderRadius: Radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  dateOptionsContent: { paddingBottom: 2 },
  cityModal: { paddingTop: 14 },
  cityOption: {
    minHeight: 48,
    borderRadius: 12,
    paddingHorizontal: 13,
    paddingVertical: 8,
    marginBottom: 8,
    justifyContent: 'space-between',
  },
  dateOption: {
    minHeight: 68,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 9,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  dateOptionActive: {
    borderColor: 'rgba(242,125,38,0.72)',
    backgroundColor: 'rgba(242,125,38,0.13)',
    ...Platform.select({
      ios: {
        shadowColor: Colors.sunset,
        shadowOpacity: 0.18,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 2 },
      },
      android: { elevation: 0 },
    }),
  },
  dateOptionAccent: {
    position: 'absolute', left: 0, top: 15, bottom: 15, width: 3,
    borderTopRightRadius: Radius.pill,
    borderBottomRightRadius: Radius.pill,
    backgroundColor: Colors.sunset,
  },
  dateNumberTile: {
    width: 46,
    height: 46,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
  },
  dateNumberTileActive: {
    backgroundColor: Colors.sunset,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  dateNumber: {
    color: Colors.turquoise,
    fontSize: 19,
    fontWeight: Typography.extrabold,
  },
  dateNumberActive: { color: '#fff' },
  dateOptionCopy: { flex: 1, marginLeft: 12 },
  dateOptionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  relativeDateBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  relativeDateBadgeActive: { backgroundColor: 'rgba(242,125,38,0.22)' },
  relativeDateText: {
    color: Colors.turquoise,
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    fontWeight: Typography.extrabold,
  },
  relativeDateTextActive: { color: '#ffb57f' },
  dateOptionIndicator: {
    width: 24,
    height: 24,
    marginLeft: 8,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateOptionIndicatorActive: {
    backgroundColor: Colors.sunset,
    borderColor: Colors.sunset,
  },
  dateOptionDay: {
    color: Colors.textPrimary,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: Typography.bold,
  },
  dateOptionText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 12,
    lineHeight: 17,
    fontWeight: Typography.medium,
  },
  dateOptionTextActive: { color: Colors.textPrimary },
  dateOptionSubtextActive: { color: 'rgba(255,255,255,0.72)' },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentSecondary: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  cityPill: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  segmented: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
  segmentInactive: daylightStyle(theme, { backgroundColor: theme.colors.chipBackground }),
  quickChip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  quickChipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
  }),
  quickTextActive: daylightStyle(theme, { color: theme.colors.accent }),
  locationBanner: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  modal: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    ...theme.shadows.elevated,
  }),
  modalHandle: daylightStyle(theme, { backgroundColor: theme.colors.borderStrong }),
  modalClose: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  dateOption: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  modalBackdrop: daylightStyle(theme, { backgroundColor: 'rgba(52,43,34,0.34)' }),
  dateOptionActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: 'rgba(242,125,38,0.62)',
  }),
  dateNumberTile: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  dateNumber: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  relativeDateBadge: daylightStyle(theme, { backgroundColor: theme.colors.accentSecondarySoft }),
  relativeDateText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  dateOptionSubtextActive: daylightStyle(theme, { color: theme.colors.textSecondary }),
  dateOptionIndicator: daylightStyle(theme, { borderColor: theme.colors.borderStrong }),
});

export default FeedScreen;
